var _excluded = ["defaultValue", "disabled", "options", "placeholder", "status", "hint", "hideLabel", "hideStatusIcon", "id", "name", "children", "required", "messages", "onChange", "value", "useNativeSelectOnMobile", "className"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import cs from 'classnames';
import FieldLabel from '../internal/field/field-label';
import FieldHint from '../internal/field/field-hint';
import FieldInput from '../internal/field/field-input';
import NativeSelect from '../native-select';
require("./select.css");
var styles = {
  "dropdown": "dropdown-RDgCJ",
  "hideMobile": "hideMobile-1oWNI",
  "inputWrapper": "inputWrapper-38Qy4",
  "inputWrapperOpen": "inputWrapperOpen-2M3Hh",
  "selectItem": "selectItem-3W3ZL",
  "selectItemCurrent": "selectItemCurrent-2C1mD",
  "dropdownError": "dropdownError-2T_-n",
  "selectItemDisabled": "selectItemDisabled-2DBmv",
  "dropdownSingleOption": "dropdownSingleOption-M2NTE",
  "dropdownDisabled": "dropdownDisabled-2j8QW",
  "dropdownSuccess": "dropdownSuccess-mTFUS",
  "hideStatusIcon": "hideStatusIcon-3l2wF",
  "menu": "menu-1zG9L",
  "menuOpen": "menuOpen-3ONoU",
  "menuItem": "menuItem-Ycasp",
  "menuRipple": "menuRipple-1Jc7B",
  "menuRippleActive": "menuRippleActive-QEUB1",
  "menuItemDisabled": "menuItemDisabled-1JzST",
  "nativeSelect": "nativeSelect-32I46"
};
var itemToString = item => {
  return item ? item.value : '';
};
var getBufferIndex = (options, buffer, previousIndex) => {
  var matches = [];
  options.forEach((option, i) => {
    if (option.disabled) {
      return;
    }
    var bufferMatchesValue = itemToString(option).trim().toLowerCase().indexOf(buffer.trim().toLowerCase()) === 0;
    if (bufferMatchesValue) {
      matches.push(i);
    }
  });
  if (matches.length) {
    var filteredMatches = matches;
    if (typeof previousIndex === 'number') {
      filteredMatches = matches.filter(match => {
        return match > previousIndex;
      });
      if (!filteredMatches.length) {
        filteredMatches = matches;
      }
    }
    return filteredMatches[0];
  }
  return -1;
};
var SelectItem = _ref => {
  var {
    label,
    disabled,
    current
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    "aria-hidden": current,
    className: cs(styles.selectItem, {
      [styles.selectItemDisabled]: disabled,
      [styles.selectItemCurrent]: current
    })
  }, label);
};
SelectItem.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  current: PropTypes.bool
};
SelectItem.defaultProps = {
  current: false,
  disabled: false,
  label: null
};
var getMatchingOption = (options, value) => {
  var selectedOption = {};
  options.forEach((option, index) => {
    if (option.value === value) {
      selectedOption = {
        option,
        index
      };
    }
  });
  return selectedOption;
};
var Select = /*#__PURE__*/forwardRef((_ref2, ref
// eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  var {
      defaultValue,
      disabled,
      options,
      placeholder,
      status,
      hint,
      hideLabel,
      hideStatusIcon,
      id,
      name,
      children,
      required,
      messages,
      onChange,
      value,
      useNativeSelectOnMobile,
      className
    } = _ref2,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var [buffer, setBuffer] = useState('');
  var [lastKeyPress, setLastKeyPress] = useState('');
  useEffect(() => {
    if (lastKeyPress) {
      var timeout = setTimeout(() => {
        setBuffer('');
      }, 500);
      return () => {
        clearTimeout(timeout);
      };
    }
    return () => {};
  }, [lastKeyPress]);
  var hintId = "".concat(id, "-hint");
  var singleOption = options.length <= 1;
  var inputDisabled = disabled || singleOption;
  var controlledSelectedItem;
  if (typeof value === 'string') {
    controlledSelectedItem = getMatchingOption(options, value).option;
  }
  var defaultSelectedItem;
  if (typeof defaultValue === 'string') {
    defaultSelectedItem = getMatchingOption(options, defaultValue).option;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, useNativeSelectOnMobile && /*#__PURE__*/React.createElement(NativeSelect, {
    className: cs(styles.nativeSelect, className),
    defaultValue: defaultValue,
    disabled: disabled,
    options: options,
    placeholder: placeholder,
    status: status,
    hint: hint,
    hideLabel: hideLabel,
    hideStatusIcon: hideStatusIcon,
    id: id,
    name: name,
    required: required,
    messages: messages,
    onChange: onChange,
    value: value
  }, children), /*#__PURE__*/React.createElement(Downshift, {
    itemToString: itemToString,
    id: id,
    labelId: "".concat(id, "-label"),
    inputId: "".concat(id, "-input"),
    menuId: "".concat(id, "-menu"),
    name: name,
    selectedItem: controlledSelectedItem,
    inputValue: value,
    initialSelectedItem: options.length === 1 ? options[0] : defaultSelectedItem,
    onInputValueChange: newValue => {
      if (newValue === value) {
        return;
      }
      if (onChange) {
        onChange({
          target: {
            name,
            id,
            value: newValue
          }
        });
      }
    }
  }, _ref3 => {
    var {
      getInputProps,
      getItemProps,
      getLabelProps,
      getMenuProps,
      highlightedIndex,
      isOpen,
      selectItemAtIndex,
      setHighlightedIndex,
      selectHighlightedItem,
      openMenu,
      toggleMenu,
      selectedItem,
      getRootProps
    } = _ref3;
    return (
      /*#__PURE__*/
      // onKeyPress handler on containing div is in place to not
      // interfere with Downshift's keyboard event handler, but i
      // still applies to the `input`.
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      React.createElement("div", {
        className: cs(styles.dropdown, {
          [styles.dropdownSuccess]: status === 'success',
          [styles.dropdownWarning]: status === 'warning',
          [styles.dropdownError]: status === 'error',
          [styles.dropdownSingleOption]: singleOption,
          [styles.dropdownDisabled]: disabled,
          [styles.hideMobile]: useNativeSelectOnMobile,
          [styles.hideStatusIcon]: hideStatusIcon
        }),
        onKeyPress: ev => {
          var {
            key
          } = ev;
          var spaceKey = key === ' ';
          var highlight = options[highlightedIndex];
          if (spaceKey) {
            ev.preventDefault();
            if (!isOpen) {
              openMenu();
              return;
            }
            if (buffer.length === 0 && !highlight.disabled) {
              selectHighlightedItem();
              return;
            }
          }
          var previousIndex;
          if (typeof highlightedIndex === 'number') {
            previousIndex = highlightedIndex;
          } else if (selectedItem) {
            previousIndex = getMatchingOption(options, selectedItem.value).index;
          }
          var newBuffer = buffer === key ? buffer : buffer + key;
          var bufferIndex = getBufferIndex(options, newBuffer, previousIndex);
          var foundBuffer = bufferIndex > -1 && bufferIndex !== highlightedIndex;
          setLastKeyPress(Date.now());
          setBuffer(newBuffer);
          if (!foundBuffer || !newBuffer.trim()) {
            return;
          }
          if (isOpen) {
            setHighlightedIndex(bufferIndex);
          } else {
            selectItemAtIndex(bufferIndex);
          }
        }
      }, /*#__PURE__*/React.createElement(FieldLabel, _extends({
        htmlFor: "".concat(id, "-select-input"),
        className: cs({
          'lll-hidden-visually': hideLabel
        }),
        required: required,
        optionalMessage: messages && messages.optional
      }, getLabelProps()), children), /*#__PURE__*/React.createElement("div", {
        className: cs(styles.inputWrapper, {
          [styles.inputWrapperOpen]: isOpen
        })
      }, /*#__PURE__*/React.createElement(FieldInput, _extends({
        htmlFor: "".concat(id, "-select-input"),
        ref: ref,
        readOnly: true,
        dropdown: true,
        "data-testid": "select-input"
      }, getInputProps({
        disabled: inputDisabled,
        onClick: toggleMenu
      }), otherProps, {
        className: className,
        noShadow: isOpen,
        placeholder: placeholder,
        "aria-describedby": hint ? hintId : null,
        required: required,
        status: status,
        hideStatusIcon: hideStatusIcon,
        isOpen: isOpen
      }, getRootProps({}, {
        suppressRefError: true
      }))), selectedItem ? /*#__PURE__*/React.createElement(SelectItem, {
        disabled: selectedItem.disabled,
        label: selectedItem.label,
        current: true
      }) : null, /*#__PURE__*/React.createElement("ul", _extends({}, getMenuProps(), {
        className: cs(styles.menu, {
          [styles.menuOpen]: isOpen
        })
      }), options.map((option, i) => {
        var highlight = highlightedIndex === i;
        return /*#__PURE__*/React.createElement("li", _extends({}, getItemProps({
          index: i,
          item: option,
          disabled: option.disabled
        }), {
          // eslint-disable-next-line react/no-array-index-key
          key: i,
          className: cs(styles.menuItem, {
            [styles.menuItemHighlighted]: highlight,
            [styles.menuItemDisabled]: option.disabled
          })
        }), /*#__PURE__*/React.createElement("div", {
          className: cs(styles.menuRipple, {
            [styles.menuRippleActive]: highlight
          })
        }, /*#__PURE__*/React.createElement(SelectItem, {
          disabled: option.disabled,
          label: option.label
        })));
      }))), /*#__PURE__*/React.createElement(FieldHint, {
        id: hintId,
        status: status
      }, hint))
    );
  }));
});
Select.propTypes = {
  /**
    Default value for uncontrolled usage.
    For controlled use, use "value"
  */
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  placeholder: PropTypes.string,
  status: PropTypes.oneOf(['success', 'warning', 'error']),
  /** Toggle to visually hide label text.
   * Text remains visible to assistive technologies. */
  hideLabel: PropTypes.bool,
  /** Toggle to hide status icon */
  hideStatusIcon: PropTypes.bool,
  /** Hint text for validation messages */
  hint: PropTypes.node,
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  /** The field's label */
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
  /** Localized strings */
  messages: PropTypes.shape({
    /** Label for the "Optional" flag */
    optional: PropTypes.string
  }),
  onChange: PropTypes.func,
  /**
    Value for controled usage.
    For uncontrolled use, use "defaultValue"
  */
  value: PropTypes.string,
  /**
    Boolean to opt into NativeSelect component on mobile to allow native rendering of options
  */
  useNativeSelectOnMobile: PropTypes.bool,
  className: PropTypes.string
};
Select.defaultProps = {
  defaultValue: undefined,
  disabled: false,
  hideLabel: false,
  hideStatusIcon: false,
  hint: null,
  messages: {},
  name: null,
  onChange: null,
  placeholder: null,
  required: true,
  status: null,
  value: undefined,
  useNativeSelectOnMobile: false,
  className: null
};
export default Select;